"use client";

/* eslint-disable indent */
import React, { useMemo } from "react";
import { FC, useCallback, useContext, useEffect, useRef, useState } from "react";
import InfiniteScroll from "react-infinite-scroller";
import { useQuery } from "react-query";
import { toast } from "react-toastify";
import { captureException } from "@sentry/react";
import { debounce } from "lodash";
import { Button } from "components/atoms/Button";
import { ButtonType } from "components/atoms/Button/types";
import GalleryImage from "components/atoms/GalleryImage";
import ActionFolderModal from "components/molecules/ActionFolderModal";
import { ADetailerModal } from "components/molecules/ADetailerModal";
import { BGRemovalModal } from "components/molecules/BGRemovalModal";
import CanvasModal from "components/molecules/CanvasModal";
import { DeleteAllConfirmationModal } from "components/molecules/DeleteAllModal";
import { DeleteConfirmationModal } from "components/molecules/DeleteModal";
import { EditModal } from "components/molecules/EditModal";
import EligibleCompetitionsModal from "components/molecules/EligibleCompetitionsModal";
import { FaceSwapModal } from "components/molecules/FaceSwapModal";
import { InpaintingModal } from "components/molecules/InpaintingModal/index";
import { LightboxWithMetadata } from "components/molecules/Lightbox";
import SearchAccordion from "components/molecules/SearchAccordion";
import ShareModal from "components/molecules/ShareModal";
import { UpscalerModal } from "components/molecules/UpscalerModal";
import { addImagesToFolder, deleteAllImages, deleteImage, getFilterFields, moveImagesToFolder, onToggleVisibility, removeImagesFromFolder, searchImages, voteOnImage } from "lib/actions";
import { AppContext } from "lib/contexts";
import { AppReducerActionType, FilterFieldDTO, IDeleteImageRequest, ImageDTO, ImageSearchQueryDTO, UserPremiumType } from "types";
import { Label, Span, SubTitle } from "utils/styling/typography.styled";
import * as Styled from "./ImageGallery.styled";
import Masonry from "react-masonry-css";
type ImageGalleryProps = {
  gallerySubtitle: string;
  userGallery: boolean;
  defaultFilters?: {
    [key: string]: string;
  };
  folderId?: string | null;
};
const ImageGallery: FC<ImageGalleryProps> = ({
  gallerySubtitle,
  userGallery,
  defaultFilters,
  folderId
}) => {
  const [selectedImages, setSelectedImages] = useState<Set<string>>(new Set());
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [showDeleteAllConfirmation, setShowDeleteAllConfirmation] = useState(false);
  const [selectedImage, setSelectedImage] = useState<ImageDTO | null>(null);
  const [isLightboxOpen, setIsLightboxOpen] = useState<boolean>(false);
  const {
    state,
    dispatch
  } = useContext(AppContext);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [imageToDelete, setImageToDelete] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [images, setImages] = useState<ImageDTO[]>([]);
  const [sort, setSort] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [filterFields, setFilterFields] = useState<FilterFieldDTO[]>([]);
  const [filters, setFilters] = useState<any>({});
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState(query);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const imageIdsSet = useRef(new Set<string>());
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [shareImageInfo, setShareImageInfo] = useState<ImageDTO | null>(null);
  const [showEligibleCompetitionsModal, setShowEligibleCompetitionsModal] = useState(false);
  const [currentImageId, setCurrentImageId] = useState<string | null>(null);
  const [isADetailerModalOpen, setIsADetailerModalOpen] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editImageId, setEditImageId] = useState<string | null>(null);
  const [isFaceSwapModalOpen, setIsFaceSwapModalOpen] = useState(false);
  const [isBGRemovalModalOpen, setIsBGRemovalModalOpen] = useState(false);
  const [isCanvasModalOpen, setCanvasModalOpen] = useState<boolean>(false);
  const [isInpaintingModalOpen, setInpaintingModalOpen] = useState(false);
  const [base64Image, setBase64Image] = useState<string | null>(null);
  const [editImage, setEditImage] = useState<ImageDTO | null>(null);
  const [isUpscalerModalOpen, setUpscalerModalOpen] = useState(false);
  const [targetFolderId, setTargetFolderId] = useState<string | null>(null);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [showRemoveFromFolderModal, setShowRemoveFromFolderModal] = useState(false);
  const [removeFolder, setRemoveFolder] = useState<string | null>(null);
  const [isMove, setIsMove] = useState<boolean>(false);
  const [sourceFolderId, setSourceFolderId] = useState<string | null>(folderId ?? null);
  const openMoveModal = () => {
    if (state.userPremiumLevel === UserPremiumType.NONE) {
      dispatch({
        type: AppReducerActionType.SHOW_SUBSCRIPTION_TABLE,
        payload: {
          ...state,
          showSubscriptionTable: true
        }
      });
      return;
    }
    if (folderId) {
      setIsMove(true);
      setSourceFolderId(folderId);
      setShowMoveModal(true);
    } else {
      setError("Source folder ID is not available.");
    }
  };
  const debouncedSetQuery = useMemo(() => debounce((value: string) => {
    setDebouncedQuery(value);
  }, 500),
  // Delay in milliseconds
  []);
  useEffect(() => {
    return () => {
      debouncedSetQuery.cancel();
    };
  }, [debouncedSetQuery]);
  const handleQueryChange = (value: React.SetStateAction<string>) => {
    setQuery(value);
    const newValue = typeof value === "function" ? value(query) : value;
    debouncedSetQuery(newValue);
  };
  const openAddModal = () => {
    if (state.userPremiumLevel === UserPremiumType.NONE) {
      dispatch({
        type: AppReducerActionType.SHOW_SUBSCRIPTION_TABLE,
        payload: {
          ...state,
          showSubscriptionTable: true
        }
      });
      return;
    }
    setIsMove(false);
    setShowMoveModal(true);
  };
  const handleMoveImages = async (folderId: string) => {
    if (!sourceFolderId) {
      setError("Source folder ID is missing.");
      return;
    }
    try {
      const result = await moveImagesToFolder(folderId, sourceFolderId, Array.from(selectedImages));
      if (result.success) {
        setImages(prevImages => prevImages.filter(img => !selectedImages.has(img.id)));
        handleDeselectAll();
        setShowMoveModal(false);
      } else {
        setError(result.error || "Failed to move images");
      }
    } catch (err: any) {
      setError(err.message || "Failed to move images");
    }
  };
  const handleAddImagesToFolder = async (folderId: string) => {
    try {
      const result = await addImagesToFolder(folderId, Array.from(selectedImages));
      if (result.success) {
        handleDeselectAll();
        setShowMoveModal(false);
      } else {
        setError(result.error || "Failed to add images");
      }
    } catch (err: any) {
      setError(err.message || "Failed to add images");
    }
  };
  const handleSubmitRemoveImagesFromFolder = async () => {
    if (!removeFolder) return;
    try {
      const result = await removeImagesFromFolder(removeFolder, Array.from(selectedImages));
      if (result.success) {
        setImages(prevImages => prevImages.filter(img => !selectedImages.has(img.id)));
        handleDeselectAll();
        setShowRemoveFromFolderModal(false);
      } else {
        setError(result.error || "Failed to remove images");
      }
    } catch (err: any) {
      setError(err.message || "Failed to remove images");
    }
  };
  const handleRemoveImagesFromFolder = async (folderId: string) => {
    setRemoveFolder(folderId);
    setShowMoveModal(false);
    setShowRemoveFromFolderModal(true);
  };
  const handleSetSelectedFolderId = (folderId: string | null) => {
    setTargetFolderId(folderId);
  };
  const handleCloseUpscalerModal = () => {
    setUpscalerModalOpen(false);
  };
  const handleUpscalerModalClick = (imageId?: string) => {
    if (state.userPremiumLevel === UserPremiumType.NONE) {
      dispatch({
        type: AppReducerActionType.SHOW_SUBSCRIPTION_TABLE,
        payload: {
          ...state,
          showSubscriptionTable: true
        }
      });
      return;
    }
    if (imageId) {
      const editedImage = images.find(image => image.id === imageId);
      if (editedImage && editedImage.imageMetadata.npcFieldsMap["Edit Type"] === "UPSCALER") {
        toast.error("This image has already been upscaled and cannot be upscaled again.");
      } else if (imageId) {
        setEditImageId(imageId);
        setUpscalerModalOpen(true);
      }
    }
  };
  const handleInpaintClick = (imageId?: string) => {
    if (state.userPremiumLevel === UserPremiumType.NONE) {
      dispatch({
        type: AppReducerActionType.SHOW_SUBSCRIPTION_TABLE,
        payload: {
          ...state,
          showSubscriptionTable: true
        }
      });
      return;
    }
    if (imageId) {
      const editedImage = images.find(image => image.id === imageId);
      if (editedImage) {
        setEditImage(editedImage);
      }
      setEditImageId(imageId);
      setCanvasModalOpen(true);
    }
  };
  const handleCloseCanvasModal = () => {
    setCanvasModalOpen(false);
  };
  const handleMaskSubmit = (maskData: string) => {
    setBase64Image(maskData); // Store the base64 data from the canvas
    setCanvasModalOpen(false); // Close the canvas modal
    setInpaintingModalOpen(true); // Open the inpainting modal
  };
  const handleCloseInpaintingModal = () => {
    setInpaintingModalOpen(false);
    setBase64Image(null); // Clear the image data
  };
  const handleCloseBGRemovalModal = () => {
    setIsBGRemovalModalOpen(false);
  };
  const handleBGRemovalClick = (imageId?: string) => {
    if (state.userPremiumLevel === UserPremiumType.NONE) {
      dispatch({
        type: AppReducerActionType.SHOW_SUBSCRIPTION_TABLE,
        payload: {
          ...state,
          showSubscriptionTable: true
        }
      });
      return;
    }
    if (imageId) {
      setEditImageId(imageId);
      setIsBGRemovalModalOpen(true);
    }
  };
  const handleCloseFaceSwapModal = () => {
    setIsFaceSwapModalOpen(false);
  };
  const handleFaceSwapClick = (imageId?: string) => {
    if (state.userPremiumLevel === UserPremiumType.NONE) {
      dispatch({
        type: AppReducerActionType.SHOW_SUBSCRIPTION_TABLE,
        payload: {
          ...state,
          showSubscriptionTable: true
        }
      });
      return;
    }
    if (imageId) {
      setEditImageId(imageId);
      setIsFaceSwapModalOpen(true);
    }
  };
  const handleADetailerClick = (imageId?: string) => {
    if (state.userPremiumLevel === UserPremiumType.NONE) {
      dispatch({
        type: AppReducerActionType.SHOW_SUBSCRIPTION_TABLE,
        payload: {
          ...state,
          showSubscriptionTable: true
        }
      });
      return;
    }
    if (imageId) {
      setEditImageId(imageId);
      setIsADetailerModalOpen(true);
    }
  };
  const handleCloseADetailerModal = () => {
    setIsADetailerModalOpen(false);
  };
  const handleEditCancel = () => {
    setShowEditModal(false);
  };
  const handleEditClick = (imageId?: string) => {
    if (state.userPremiumLevel === UserPremiumType.NONE) {
      dispatch({
        type: AppReducerActionType.SHOW_SUBSCRIPTION_TABLE,
        payload: {
          ...state,
          showSubscriptionTable: true
        }
      });
      return;
    }
    if (imageId) {
      setEditImageId(imageId);
      setShowEditModal(true);
    }
  };
  const handleCompetitionsClick = (imageId: string) => {
    setCurrentImageId(imageId);
    setShowEligibleCompetitionsModal(true);
  };
  const handleCloseEligibleCompetitionsModal = () => {
    setShowEligibleCompetitionsModal(false);
    setCurrentImageId(null);
    dispatch({
      type: AppReducerActionType.TOGGLE_OVERLAY_VISIBILITY,
      payload: {
        ...state,
        isOverlayVisible: false
      }
    });
  };
  const handleShareClick = (e: React.MouseEvent, imageId?: string) => {
    e.preventDefault();
    e.stopPropagation();
    const selectedImage = images.find(image => image.id === imageId);
    if (selectedImage) {
      setShareImageInfo(selectedImage);
      setIsShareModalOpen(true);
    }
  };
  const handleCloseShareModal = () => {
    setIsShareModalOpen(false);
  };
  useEffect(() => {
    const fetchFilters = async () => {
      const fields = await getFilterFields();
      setFilterFields(fields);
    };
    fetchFilters();
  }, []);
  useEffect(() => {
    if (defaultFilters) {
      setFilters(defaultFilters);
    }
  }, [defaultFilters]);
  const handleImageClick = useCallback((image: ImageDTO) => {
    setSelectedImage(image);
    setIsLightboxOpen(true);
  }, []);
  const handleIndexChange = (newIndex: number) => {
    setSelectedImage(images[newIndex]);
  };
  const handleLightboxClose = () => {
    setIsLightboxOpen(false);
    setSelectedImage(null);
  };
  const handleSelectImage = (imageId: string) => {
    setIsSelectionMode(true); // Automatically enable selection mode
    setSelectedImages(prev => {
      const newSelectedImages = new Set(prev);
      if (newSelectedImages.has(imageId)) {
        newSelectedImages.delete(imageId);
      } else {
        newSelectedImages.add(imageId);
      }
      return newSelectedImages;
    });
  };
  const handleDeselectAll = () => {
    setIsSelectionMode(false);
    setError("");
    setSelectedImages(new Set());
  };
  const handleDeleteAll = async () => {
    try {
      const dataPayload: IDeleteImageRequest = {
        imageIds: Array.from(selectedImages)
      };
      await deleteAllImages(dataPayload);
      setImages(prev => prev.filter(image => !selectedImages.has(image.id)));
      setShowDeleteAllConfirmation(false);
      handleDeselectAll();
    } catch (error: any) {
      captureException("Failed to delete images", error);
      setShowDeleteAllConfirmation(false);
      handleDeselectAll();
    }
  };
  const fetchImages = async ({
    page,
    sort,
    filters,
    query
  }: {
    page: number;
    sort: string;
    filters: any;
    query: string;
  }) => {
    try {
      const searchQuery: ImageSearchQueryDTO = {
        query,
        sortField: sort,
        sortOrder: sortOrder,
        filters,
        page,
        pageSize: 15,
        userGallery: userGallery
      };
      const response = await searchImages(searchQuery);
      if (response.error) {
        setError(typeof response.error === "string" ? response.error : response.error.message);
        setHasMore(false);
        return {
          error: response.error
        };
      }
      if (response.image && response.image.length === 0) {
        setHasMore(false);
      }
      return response;
    } catch (e) {
      captureException(e);
      setError("Failed to get images");
      setHasMore(false);
      return {
        error: "Failed to get images"
      };
    }
  };
  const {
    isLoading,
    refetch
  } = useQuery(["images", page, sort, sortOrder, filters, debouncedQuery], () => fetchImages({
    page,
    sort,
    filters,
    query: debouncedQuery
  }), {
    keepPreviousData: true,
    onSuccess: newData => {
      if (newData.image && Array.isArray(newData.image)) {
        const uniqueImages = newData.image.filter((image: ImageDTO) => !imageIdsSet.current.has(image.id));
        uniqueImages.forEach((image: ImageDTO) => imageIdsSet.current.add(image.id));
        setImages(prev => [...prev, ...uniqueImages]);
        setHasMore(uniqueImages.length > 0);
      } else {
        // Ensure images is set to an empty array when no data is returned
        setImages([]);
        setHasMore(false);
      }
      setLoadingMore(false);
    },
    onError: () => {
      // Ensure images is set to an empty array on error
      setImages([]);
      setHasMore(false);
      setLoadingMore(false);
    }
  });
  const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>) => {
    const {
      name,
      value,
      type
    } = event.target as HTMLInputElement | HTMLSelectElement;
    const checked = type === "checkbox" ? (event.target as HTMLInputElement).checked : false;
    setFilters((prevFilters: any) => ({
      ...prevFilters,
      [name]: type === "checkbox" ? checked : value
    }));
  };
  const loadMore = () => {
    if (!isLoading && hasMore && !loadingMore) {
      setLoadingMore(true);
      setPage(prevPage => prevPage + 1);
    }
  };
  useEffect(() => {
    setImages([]);
    setPage(0);
    setHasMore(true);
    imageIdsSet.current.clear();
  }, [sort, sortOrder, filters, debouncedQuery]);
  useEffect(() => {
    if (page > 0) {
      refetch();
    }
  }, [page, refetch]);
  const handleVote = async (imageId: string) => {
    if (!state.isLoggedIn) {
      dispatch({
        type: AppReducerActionType.SHOW_AUTH_MODAL,
        payload: {
          ...state,
          showAuthModal: true
        }
      });
      return;
    }
    const currentImage = images.find(img => img.id === imageId);
    if (!currentImage) return;
    const newVoteStatus = !currentImage.isVotedByUser;
    try {
      await voteOnImage(imageId, newVoteStatus);
      setImages(prev => prev.map(img => img.id === imageId ? {
        ...img,
        isVotedByUser: newVoteStatus,
        voteCount: img.voteCount + (newVoteStatus ? 1 : -1)
      } : img));
      if (selectedImage?.id === imageId) {
        setSelectedImage(prev => {
          if (!prev) return null;
          return {
            ...prev,
            isVotedByUser: newVoteStatus,
            voteCount: prev.voteCount + (newVoteStatus ? 1 : -1)
          };
        });
      }
    } catch (error: any) {
      captureException("Failed to update vote status.", error);
    }
  };
  const prepareForDelete = (imageId: string) => {
    setImageToDelete(imageId);
    setShowDeleteConfirmation(true);
  };
  const handleConfirmDelete = async (imageId?: string) => {
    if (!imageToDelete && !imageId) return;
    try {
      let newImageDtos;
      if (imageId && typeof imageId === "string") {
        await deleteImage(imageId);
        newImageDtos = images.filter(img => img.id !== imageId);
      } else if (imageToDelete) {
        await deleteImage(imageToDelete);
        newImageDtos = images.filter(img => img.id !== imageToDelete);
      }
      if (!newImageDtos) return;
      setImages(newImageDtos);
      setShowDeleteConfirmation(false);
      setImageToDelete(null);
    } catch (error: any) {
      captureException("Failed to delete image", error);
      setShowDeleteConfirmation(false);
      setImageToDelete(null);
    }
  };
  const handleCancelDelete = () => {
    setShowDeleteConfirmation(false);
    setImageToDelete(null);
  };
  const isUserImage = (imageUsername: string) => state.username === imageUsername;
  const handleToggle = (imageId: string) => {
    const currentVisibility = images.find(img => img.id === imageId)?.isPublic;
    const newVisibility = !currentVisibility;
    onToggleVisibility(imageId, newVisibility).then(() => {
      setImages(prev => prev.map(image => image.id === imageId ? {
        ...image,
        isPublic: newVisibility
      } : image));
    }).catch(error => {
      captureException("Failed to update visibility status.", error);
    });
  };
  const breakpointColumnsObj = {
    default: 6,
    1400: 4,
    1024: 3,
    768: 2,
    576: 1
  };
  const renderImage = (image: ImageDTO) => <GalleryImage key={image.id} item={image} isUserImage={isUserImage} onImageClick={handleImageClick} handleToggle={handleToggle} handleEditClick={handleEditClick} handleADetailerClick={handleADetailerClick} handleFaceSwapClick={handleFaceSwapClick} handleBGRemovalClick={handleBGRemovalClick} handleInpaintClick={handleInpaintClick} handleUpscaleClick={handleUpscalerModalClick} prepareForDelete={prepareForDelete} handleVote={handleVote} isSelectionMode={isSelectionMode} handleSelectImage={handleSelectImage} isSelected={selectedImages.has(image.id)} userGallery={userGallery} handleShareClick={handleShareClick} handleCompetitionsClick={() => handleCompetitionsClick(image.id)} showDeleteConfirmation={showDeleteConfirmation} data-sentry-element="GalleryImage" data-sentry-component="renderImage" data-sentry-source-file="index.tsx" />;
  return <Styled.ImageGalleryWrapper data-sentry-element="unknown" data-sentry-component="ImageGallery" data-sentry-source-file="index.tsx">
            <SubTitle data-sentry-element="SubTitle" data-sentry-source-file="index.tsx">{gallerySubtitle}</SubTitle>
            <SearchAccordion filterFields={filterFields} filters={filters} handleFilterChange={handleFilterChange} query={query} setQuery={handleQueryChange} sort={sort} setSort={setSort} sortOrder={sortOrder} setSortOrder={setSortOrder} data-sentry-element="SearchAccordion" data-sentry-source-file="index.tsx">
                {userGallery && <>
                        {!isSelectionMode && images.length > 0 && <Styled.SingleButtonContainer>
                                <Button variant={ButtonType.PRIMARY} onClick={() => setIsSelectionMode(true)}>
                                    Enable Selection Mode
                                </Button>
                            </Styled.SingleButtonContainer>}
                        {isSelectionMode && <Styled.ButtonContainer>
                                {folderId && <Button variant={ButtonType.PRIMARY} onClick={() => openMoveModal()} disabled={selectedImages.size === 0 || !folderId}>
                                        Move Selected to Folder
                                    </Button>}
                                <Button variant={ButtonType.PRIMARY} onClick={() => openAddModal()} disabled={selectedImages.size === 0}>
                                    Add Selected to Folder
                                </Button>
                                {folderId && <Button variant={ButtonType.PRIMARY} onClick={() => handleRemoveImagesFromFolder(folderId!)} disabled={selectedImages.size === 0 || !folderId}>
                                        Remove Selected from Folder
                                    </Button>}
                                <Button variant={ButtonType.PRIMARY} onClick={() => setShowDeleteAllConfirmation(true)}>
                                    Delete Selected Images
                                </Button>
                                <Button variant={ButtonType.ERROR} onClick={handleDeselectAll}>
                                    Cancel
                                </Button>
                            </Styled.ButtonContainer>}
                    </>}
                {error && <div>{error}</div>}
                {!hasMore && images.length === 0 && <Label>No images to display</Label>}

                <InfiniteScroll pageStart={0} loadMore={loadMore} hasMore={hasMore} loader={<Styled.LoaderWrapper key={0}>
                            Loading ...
                        </Styled.LoaderWrapper>} style={{
        width: "100%"
      }} data-sentry-element="InfiniteScroll" data-sentry-source-file="index.tsx">
                    {Array.isArray(images) && images.length > 0 && <Masonry breakpointCols={breakpointColumnsObj} className="flex w-full gap-4" columnClassName="bg-clip-padding align-center">
                            {images.map(renderImage)}
                        </Masonry>}
                </InfiniteScroll>
                {!hasMore && <Button type="submit" variant={ButtonType.PRIMARY} disabled={false} onClick={() => {
        setHasMore(true);
        loadMore();
      }}>
                        <Span>Load More</Span>
                    </Button>}
            </SearchAccordion>
            <LightboxWithMetadata imageDtos={images} initialImageId={selectedImage?.id} onClose={handleLightboxClose} open={isLightboxOpen} onToggleVisibility={handleToggle} onVote={handleVote} handleConfirmDelete={handleConfirmDelete} onIndexChange={handleIndexChange} data-sentry-element="LightboxWithMetadata" data-sentry-source-file="index.tsx" />
            <DeleteConfirmationModal isOpen={showDeleteConfirmation} onConfirm={handleConfirmDelete} onCancel={handleCancelDelete} text="image" data-sentry-element="DeleteConfirmationModal" data-sentry-source-file="index.tsx" />
            <DeleteConfirmationModal isOpen={showRemoveFromFolderModal} onConfirm={handleSubmitRemoveImagesFromFolder} onCancel={() => setShowRemoveFromFolderModal(false)} text="images from folder" data-sentry-element="DeleteConfirmationModal" data-sentry-source-file="index.tsx" />
            <DeleteAllConfirmationModal isOpen={showDeleteAllConfirmation} onConfirm={handleDeleteAll} onCancel={() => setShowDeleteAllConfirmation(false)} deleteCount={selectedImages.size} data-sentry-element="DeleteAllConfirmationModal" data-sentry-source-file="index.tsx" />
            <ActionFolderModal open={showMoveModal} onClose={() => setShowMoveModal(false)} onAction={isMove ? handleMoveImages : handleAddImagesToFolder} selectedFolderId={targetFolderId} setSelectedFolderId={handleSetSelectedFolderId} text={isMove ? "Move Images to Folder" : "Add Images to Folder"} data-sentry-element="ActionFolderModal" data-sentry-source-file="index.tsx" />
            <ShareModal isOpen={isShareModalOpen} image={shareImageInfo} onClose={handleCloseShareModal} data-sentry-element="ShareModal" data-sentry-source-file="index.tsx" />
            {currentImageId && <EligibleCompetitionsModal isOpen={showEligibleCompetitionsModal} onCancel={handleCloseEligibleCompetitionsModal} imageId={currentImageId} />}
            {editImageId && <ADetailerModal isOpen={isADetailerModalOpen} onClose={handleCloseADetailerModal} imageId={editImageId} isPremium={false} />}
            {editImageId && <EditModal isOpen={showEditModal} onCancel={handleEditCancel} imageId={editImageId} />}
            {editImageId && <FaceSwapModal isOpen={isFaceSwapModalOpen} onClose={handleCloseFaceSwapModal} imageId={editImageId} />}
            {editImageId && <BGRemovalModal isOpen={isBGRemovalModalOpen} onClose={handleCloseBGRemovalModal} imageId={editImageId} />}
            {editImageId && <CanvasModal isOpen={isCanvasModalOpen} onClose={handleCloseCanvasModal} imageDto={editImage} onMaskSubmit={handleMaskSubmit} />}
            {editImageId && editImage && <InpaintingModal isOpen={isInpaintingModalOpen} onCancel={handleCloseInpaintingModal} base64Image={base64Image!} originalImage={editImage.image} imageId={editImageId} handleInpaintClick={handleInpaintClick} />}
            {editImageId && <UpscalerModal isOpen={isUpscalerModalOpen} onClose={handleCloseUpscalerModal} imageId={editImageId} />}
        </Styled.ImageGalleryWrapper>;
};
export default React.memo(ImageGallery);